import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toastManager from '../../utils/toastManager';
import AddServer from './AddServer/AddServer';
import RoomTable from '../Rooms/RoomTable';
import JoinServer from './JoinServer/JoinServer';
import GameResultsBox from '../GameResults/GameResultsBox';
import { ModeSelector, GAME_MODE_DEFS } from '../GameModes/ModeSelector';
import { SocketContext } from '../../App';
import { useAuth } from '../../context/AuthContext';
import styles from './LoginPage.module.css';

const BOT_COUNTS = [
  { label: '1v1 Bot',   maxPlayers: 2 },
  { label: '1v2 Bots',  maxPlayers: 3 },
  { label: '1v3 Bots',  maxPlayers: 4 },
];

const LoginPage = () => {
  const socket    = useContext(SocketContext);
  const navigate  = useNavigate();
  const location  = useLocation();
  const { canCreateGames } = useAuth();
  const canHost   = canCreateGames;

  /* ── State ──────────────────────────────────────────────────── */
  const [playerName, setPlayerName]         = useState('');
  const [activeTab, setActiveTab]           = useState('play');
  const [showHostModal, setShowHostModal]   = useState(false);
  const [hostStep, setHostStep]             = useState(1);
  const [selectedMode, setSelectedMode]     = useState('classic');
  const [botMaxPlayers, setBotMaxPlayers]   = useState(2);
  const [computerMode, setComputerMode]     = useState('classic');
  const [computerTimerMinutes, setComputerTimerMinutes] = useState(2);
  const [botLoading, setBotLoading]         = useState(false);

  /* ── Init ───────────────────────────────────────────────────── */
  useEffect(() => {
    const stored = localStorage.getItem('playerName');
    if (stored) setPlayerName(stored);
  }, []);

  /* ── Auto-open host modal from GameModesPage ─────────────────── */
  useEffect(() => {
    if (location.state?.openHost) {
      openHostModal(location.state.mode || null);
      navigate('/', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Listen for navbar "Host" button event ───────────────────── */
  useEffect(() => {
    const handler = () => { if (canHost) openHostModal(); else navigate('/auth/login'); };
    window.addEventListener('open-host-modal', handler);
    return () => window.removeEventListener('open-host-modal', handler);
  }, [canHost]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Helpers ─────────────────────────────────────────────────── */
  const openHostModal = (preMode = null) => {
    if (preMode) { setSelectedMode(preMode); setHostStep(2); }
    else         { setHostStep(1); }
    setShowHostModal(true);
  };

  const closeHostModal = () => { setShowHostModal(false); setHostStep(1); };

  const handlePlayVsBot = () => {
    const name = playerName || localStorage.getItem('playerName');
    if (!name) { toastManager.info('Please set your name first'); return; }
    if (!socket) return;
    setBotLoading(true);
    socket.emit('player:createBotGame', {
      name,
      maxPlayers: botMaxPlayers,
      gameMode: computerMode,
      timerMinutes: computerMode === 'timer' ? computerTimerMinutes : 0,
    });
    setTimeout(() => setBotLoading(false), 8000);
  };

  const modeDef = GAME_MODE_DEFS.find(m => m.value === selectedMode) || GAME_MODE_DEFS[0];

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <div className={styles.page}>
      <div className={styles.inner}>

        {/* ── Hero strip ── */}
        <div className={styles.hero}>
          <div className={styles.heroLeft}>
            <div className={styles.heroGreeting}>Welcome back</div>
            <div className={styles.heroName}>
              {playerName ? <><span>{playerName}</span> — ready to roll?</> : 'Set your name to start playing'}
            </div>
          </div>
          <div className={styles.heroRight}>
            <button
              onClick={() => navigate('/game-modes')}
              style={{ background:'rgba(139,92,246,0.15)', color:'#8b5cf6', border:'1px solid rgba(139,92,246,0.3)', borderRadius:9, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer', boxShadow:'none', transition:'all 0.2s' }}
            >
              🎲 Game Modes
            </button>
            {canHost ? (
              <button
                onClick={() => openHostModal()}
                style={{ background:'linear-gradient(135deg,#ff6b00,#ff9500)', color:'#fff', border:'none', borderRadius:9, padding:'8px 18px', fontSize:13, fontWeight:800, cursor:'pointer', boxShadow:'0 0 14px rgba(255,107,0,0.4)', transition:'all 0.2s' }}
              >
                + Host Room
              </button>
            ) : (
              <Link
                to="/auth/login"
                style={{ background:'rgba(255,107,0,0.1)', color:'#ff6b00', border:'1px solid rgba(255,107,0,0.3)', borderRadius:9, padding:'8px 16px', fontSize:13, fontWeight:700, textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6 }}
                title="Only API Manager can host games"
              >
                🔒 Sign In to Host
              </Link>
            )}
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className={styles.tabBar}>
          {[
            { id:'play',    label:'🏠 Play'       },
            { id:'bot',     label:'🎮 Computer'   },
            { id:'results', label:'📊 Results'    },
          ].map(t => (
            <button
              key={t.id}
              className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(t.id)}
            >{t.label}</button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className={styles.content}>

          {/* ── PLAY TAB ── */}
          {activeTab === 'play' && (
            <div className={styles.playGrid}>
              {/* Left: room list */}
              <JoinServer />
              {/* Right: sidebar card */}
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <RoomTable hideHeader />
              </div>
            </div>
          )}

          {/* ── BOT TAB ── */}
          {activeTab === 'bot' && (
            <div className={styles.botCard}>
              <div style={{ fontSize:48 }}>🎮</div>
              <h2 className={styles.botCardTitle}>Computer Match</h2>
              <p className={styles.botCardDesc}>
                Start instantly against smart players with random names. Choose the match type and player count.
              </p>
              <div style={{ width:'100%' }}>
                <div style={{ color:'rgba(255,255,255,0.5)', fontSize:11, fontWeight:600, letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:8 }}>Game type</div>
                <div className={styles.botModeGroup} style={{ marginBottom: 14 }}>
                  {[['classic', 'Classic'], ['timer', '⏱ Timer']].map(([value, label]) => (
                    <button
                      key={value}
                      className={`${styles.botModeBtn} ${computerMode === value ? styles.botModeBtnActive : ''}`}
                      onClick={() => setComputerMode(value)}
                    >{label}</button>
                  ))}
                </div>
                {computerMode === 'timer' && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(5,minmax(0,1fr))', gap:6, marginBottom:14 }}>
                    {[2,4,6,8,10].map(minutes => (
                      <button
                        key={minutes}
                        onClick={() => setComputerTimerMinutes(minutes)}
                        style={{ padding:'9px 2px', borderRadius:8, border:computerTimerMinutes === minutes ? '2px solid #facc15' : '1px solid rgba(255,255,255,0.15)', background:computerTimerMinutes === minutes ? '#0891b2' : 'rgba(255,255,255,0.05)', color:'#fff', fontWeight:800, cursor:'pointer', boxShadow:'none' }}
                      >{minutes}m</button>
                    ))}
                  </div>
                )}
                <div style={{ color:'rgba(255,255,255,0.5)', fontSize:11, fontWeight:600, letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:8 }}>Players</div>
                <div className={styles.botModeGroup}>
                  {BOT_COUNTS.map(opt => (
                    <button
                      key={opt.maxPlayers}
                      className={`${styles.botModeBtn} ${botMaxPlayers === opt.maxPlayers ? styles.botModeBtnActive : ''}`}
                      onClick={() => setBotMaxPlayers(opt.maxPlayers)}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              <button
                className={styles.botPlayBtn}
                onClick={handlePlayVsBot}
                disabled={botLoading}
              >
                {botLoading ? '⏳ Starting…' : '▶ Start Match'}
              </button>
              <p className={styles.botNote}>The match starts instantly. Opponents appear with normal random player names.</p>
            </div>
          )}

          {/* ── RESULTS TAB ── */}
          {activeTab === 'results' && (
            <div className={styles.resultsContent}>
              <GameResultsBox />
            </div>
          )}

        </div>
      </div>

      {/* ── HOST MODAL (2-step) ── */}
      {showHostModal && (
        <div className={styles.overlayPopup} onClick={closeHostModal}>
          <div className={styles.popupBox} onClick={e => e.stopPropagation()}>

            {/* Step 1: Mode picker */}
            {hostStep === 1 && (
              <>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
                  <div>
                    <h3 style={{ margin:0, color:'#fff', fontSize:17, fontWeight:800 }}>🎲 Choose Game Mode</h3>
                    <div style={{ color:'rgba(255,255,255,0.4)', fontSize:11, marginTop:2 }}>Select how you want to play</div>
                  </div>
                  <button onClick={closeHostModal} style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'white', padding:'5px 12px', cursor:'pointer', fontSize:13, boxShadow:'none', flexShrink:0 }}>✕</button>
                </div>
                {/* Let the popup handle scrolling — no inner maxHeight cap */}
                <ModeSelector value={selectedMode} onChange={setSelectedMode} maxHeight={99999} />
                <div style={{ display:'flex', gap:10, marginTop:14, position:'sticky', bottom:0, background:'rgba(13,19,50,0.98)', padding:'10px 0 2px', backdropFilter:'blur(8px)' }}>
                  <button onClick={closeHostModal} style={{ flex:1, padding:'11px 0', borderRadius:10, border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.05)', color:'white', cursor:'pointer', fontSize:14, boxShadow:'none' }}>Cancel</button>
                  <button
                    onClick={() => setHostStep(2)}
                    style={{ flex:2, padding:'11px 0', borderRadius:10, border:'none', background:`linear-gradient(135deg,${modeDef.color},${modeDef.color}cc)`, color:'white', fontWeight:700, cursor:'pointer', fontSize:14, boxShadow:`0 4px 14px ${modeDef.color}55` }}
                  >
                    {modeDef.emoji} Continue → {modeDef.label}
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Room details */}
            {hostStep === 2 && (
              <>
                {/* Step 2 header */}
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, flexWrap:'wrap' }}>
                  <button onClick={() => setHostStep(1)} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'white', padding:'5px 10px', cursor:'pointer', fontSize:13, boxShadow:'none', flexShrink:0 }}>← Back</button>
                  {/* Mode pill — shows selected mode prominently */}
                  <div style={{ flex:1, display:'flex', alignItems:'center', gap:7, padding:'5px 12px', background:`${modeDef.color}22`, border:`1px solid ${modeDef.color}55`, borderRadius:20, minWidth:0 }}>
                    <span style={{ fontSize:18 }}>{modeDef.emoji}</span>
                    <div style={{ minWidth:0 }}>
                      <div style={{ color:'#fff', fontWeight:700, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{modeDef.label}</div>
                      <div style={{ color:'rgba(255,255,255,0.45)', fontSize:10, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{modeDef.en}</div>
                    </div>
                  </div>
                  <button onClick={closeHostModal} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'white', padding:'5px 10px', cursor:'pointer', fontSize:13, boxShadow:'none', flexShrink:0 }}>✕</button>
                </div>
                <AddServer defaultGameMode={selectedMode} onCreated={closeHostModal} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
